import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const Lightbox = ({ images, index, onClose, onNav }) => {
  const { t } = useLang();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const img = images[index];

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Gallery image viewer"
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-midnight/95 p-6 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <button
        onClick={onClose}
        aria-label={t.lightbox.close}
        data-testid="lightbox-close-btn"
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center text-ivory/70 transition-colors duration-300 hover:text-gold"
      >
        <X size={26} strokeWidth={1.25} />
      </button>

      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <motion.img
          key={index}
          src={img.src}
          alt={img.alt}
          className="max-h-[76vh] w-auto max-w-full object-contain"
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        />
        <p className="mt-5 text-center font-serif text-base italic text-gold/90">
          {img.caption}
        </p>
        <p className="mt-2 text-center font-sans text-[10px] tracking-[0.35em] text-ivory/40">
          {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
        </p>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label={t.lightbox.prev}
        data-testid="lightbox-prev-btn"
        className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-gold/30 text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        <ChevronLeft size={22} strokeWidth={1.25} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label={t.lightbox.next}
        data-testid="lightbox-next-btn"
        className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-gold/30 text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        <ChevronRight size={22} strokeWidth={1.25} />
      </button>
    </motion.div>
  );
};

export default Lightbox;
